import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import api, { API } from "../lib/api";
import Logo from "../components/Logo";
import { ArrowLeft, ArrowRight, Sparkles, GitFork, Star, ShieldCheck, UserPlus, UserCheck, Users2 } from "lucide-react";

/**
 * Creator Profile — public page at /creators/{user_id}. Shows a creator's
 * bio-ish header, aggregate stats, and every public composed model they own.
 */
export default function CreatorProfile() {
  const { user_id } = useParams();
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [followState, setFollowState] = useState({ is_following: false });
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const isAuthed = !!localStorage.getItem("token");
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null")?.user_id; } catch { return null; }
  })();
  const isSelf = currentUserId === user_id;

  useEffect(() => {
    axios.get(`${API}/creators/${user_id}`, { withCredentials: false })
      .then((r) => setProfile(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Creator not found"));
  }, [user_id]);

  useEffect(() => {
    if (!isAuthed || isSelf || !user_id) return;
    api.get(`/follow/status/${user_id}`).then((r) => setFollowState(r.data)).catch(() => {});
  }, [user_id, isAuthed, isSelf]);

  const toggleFollow = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    const wasFollowing = followState.is_following;
    // Optimistic
    setFollowState((f) => ({ ...f, is_following: !wasFollowing }));
    setProfile((p) => p ? { ...p, followers: Math.max(0, p.followers + (wasFollowing ? -1 : 1)) } : p);
    try {
      if (wasFollowing) {
        await api.delete(`/follow/${user_id}`);
      } else {
        await api.post(`/follow/${user_id}`);
      }
    } catch (e) {
      // Revert
      setFollowState((f) => ({ ...f, is_following: wasFollowing }));
      setProfile((p) => p ? { ...p, followers: Math.max(0, p.followers + (wasFollowing ? 1 : -1)) } : p);
      alert(e.response?.data?.detail || "Follow failed");
    } finally {
      setTimeout(() => { busyRef.current = false; setBusy(false); }, 400);
    }
  };

  if (err) return <div className="max-w-3xl mx-auto p-8"><div className="brut-card p-6 text-red-600">{err}</div></div>;
  if (!profile) return <div className="max-w-3xl mx-auto p-8 italic text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/"><Logo size={36} /></Link>
        <div className="flex items-center gap-3">
          <Link to="/creators" className="text-sm font-heading font-bold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft size={12} strokeWidth={2.5} /> All creators
          </Link>
          <Link to={isAuthed ? "/studio" : "/login"} className="brut-btn text-sm" data-testid="profile-cta-top">
            {isAuthed ? "Open Studio" : "Try AiMedley"} <ArrowRight size={12} strokeWidth={2.5} />
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="brut-card p-6 mb-6" style={{ boxShadow: "6px 6px 0 #F59E0B" }} data-testid="profile-header">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-24 h-24 rounded-full border-2 border-slate-900 bg-amber-200 flex items-center justify-center font-heading font-bold text-3xl shrink-0" data-testid="profile-initials">
              {profile.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-2">
                <Users2 size={12} strokeWidth={2.5} /> Creator
              </div>
              <h1 className="font-heading text-4xl font-bold" data-testid="profile-name">{profile.name}</h1>
              <div className="text-xs text-slate-500 mt-1">
                {profile.followers} follower{profile.followers === 1 ? "" : "s"} · {profile.following} following · joined {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : "recently"}
              </div>
            </div>
            <div className="shrink-0">
              {isSelf ? (
                <Link to="/studio" className="brut-btn text-sm" data-testid="profile-open-studio">
                  <Sparkles size={12} strokeWidth={3} /> Open your Studio
                </Link>
              ) : isAuthed ? (
                <button onClick={toggleFollow} disabled={busy} className={`brut-btn text-sm ${followState.is_following ? "bg-slate-900 text-white" : ""}`} data-testid="profile-follow">
                  {followState.is_following ? <><UserCheck size={12} strokeWidth={3} /> Following</> : <><UserPlus size={12} strokeWidth={3} /> Follow</>}
                </button>
              ) : (
                <Link to="/login" className="brut-btn text-sm bg-white text-slate-900" data-testid="profile-follow-signin">
                  <UserPlus size={12} strokeWidth={3} /> Sign in to follow
                </Link>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-5" data-testid="profile-stats">
            <Stat icon={Sparkles} label="Models"  value={profile.public_models} />
            <Stat icon={GitFork}  label="Forks"   value={profile.total_forks} />
            <Stat icon={Star}     label="Stars"   value={profile.total_stars} />
          </div>
        </div>

        <h3 className="font-heading text-xl font-bold mb-3">Public models</h3>
        {profile.models.length === 0 ? (
          <div className="brut-card p-6 text-center text-slate-500 italic">This creator hasn't shipped a public model yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="profile-models">
            {profile.models.map((m) => (
              <Link
                key={m.ai_model_id}
                to={`/studio/${m.ai_model_id}`}
                className="brut-card p-4 hover:-translate-y-0.5 transition-transform"
                style={{ boxShadow: "3px 3px 0 #F59E0B" }}
                data-testid={`profile-model-${m.ai_model_id}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-xl shrink-0">{m.avatar}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm truncate">{m.name}</div>
                    <div className="text-[10px] font-mono text-slate-500 truncate">{m.base_brain_id}</div>
                  </div>
                </div>
                {m.description && <p className="text-xs text-slate-600 line-clamp-2 mb-2">{m.description}</p>}
                <div className="flex items-center gap-2 flex-wrap text-[10px]">
                  <span className="inline-flex items-center gap-0.5 text-slate-500"><GitFork size={9} /> {m.fork_count}</span>
                  <span className="inline-flex items-center gap-0.5 text-slate-500"><Star size={9} /> {m.star_count}</span>
                  {m.last_grade?.average?.overall !== undefined && (
                    <span className="inline-flex items-center gap-0.5" style={{ color: gradeColor(m.last_grade.average.overall) }}>
                      <ShieldCheck size={9} strokeWidth={3} /> {m.last_grade.average.overall}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border-2 border-slate-200 rounded-xl py-3 px-2 text-center">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        <Icon size={10} strokeWidth={3} /> {label}
      </div>
      <div className="font-mono font-bold text-2xl leading-tight">{value}</div>
    </div>
  );
}

function gradeColor(v) { if (v >= 80) return "#10B981"; if (v >= 50) return "#F59E0B"; return "#EF4444"; }
